import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { getAIContextPanelResources } from '@jira/router-resources__service-desk-ai-context-panel/src/getAIContextPanelResources.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { SidebarIdCsmApp } from '@jira/navigation-apps__constants/src/SidebarIdCsmApp.tsx';
import { getCustomerServiceL1NavigationResources } from '@jira/router-resources__customer-service-l1-navigation/src/getCustomerServiceL1NavigationResources.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { csmAppSidebarEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-csm-app/src/csmAppSidebarEntryPoint.entrypoint.tsx';
import { notFoundPageEntrypoint } from '@jira/router-routes__not-found-entrypoint/src/ui/error-pages/not-found/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	getInsightResources,
	getQueuesResources,
	getTriageButtonPermissionResource,
} from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { horizontalNavQueueEntrypoint } from '@jira/navigation-apps__horizontal-nav-jsm-queue/src/horizontalNavQueueEntrypoint.entrypoint.tsx';
import { issueListSsrResource } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { servicedeskIsItsmProjectResource } from '@jira/router-resources__service-desk-is-itsm-project/src/useIsItsmTemplateProject.tsx';
import { csmCustomIssueRoute } from '@jira/router-routes__servicedesk-queue-routes/src/csmCustomIssueRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import { getDraftReplyPillsResource } from '@jira/router-resources__service-desk-draft-reply/src/getDraftReplyPillsResource.tsx';
import { getJsmIssueViewRelatedArticlesRouteResource } from '@jira/router-resources__jsm-issue-view/src/getJsmIssueViewRelatedArticlesRouteResource.tsx';
import { queuesAgentAndIssueViewEntryPoint } from '@jira/spa-apps__servicedesk-queues-agent-and-issue-view/src/queuesAgentAndIssueViewEntryPoint.entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';

import { QueueLoadingSkeleton } from './ui/queue/index.tsx';

const csmCustomIssuePageEntryPoint = createPageEntryPoint({
	main: queuesAgentAndIssueViewEntryPoint,
	horizontalNavigation: horizontalNavQueueEntrypoint,
	leftSidebar: {
		sidebar: csmAppSidebarEntryPoint,
		isProjectsExpanded: true,
	},
});

// It is important to keep these in sync:
//   - customQueueRouteEntry
//   - customIssueRouteEntry
//   - csmCustomQueueRouteEntry
//   - csmCustomIssueRouteEntry
// they are rendering the exact same view. This is done to keep the state
// consistent when switching between the two routes.
export const csmCustomIssueRouteEntry: Route = createEntry(csmCustomIssueRoute, () => ({
	layout: composeLayouts(
		serviceProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				params: [
					{
						key: 'issueKey',
					},
				],
			},
		]),
	),

	navigation: {
		sidebarId: SidebarIdCsmApp,
	},

	skeleton: QueueLoadingSkeleton,

	resources: [
		...getCustomerServiceL1NavigationResources(),
		issueListSsrResource,
		...getQueuesResources(),
		...getIssueViewResources(),
		...getInsightResources(),
		...getTriageButtonPermissionResource(),
		getConsolidationStateResource(),
		servicedeskIsItsmProjectResource,
		...getAIContextPanelResources(),
		...getResolutionPlanSSRResource(),
		...getDraftReplyPillsResource(),
		getJsmIssueViewRelatedArticlesRouteResource(),
	],

	afterPaint: [LazyIssueApp],
	group: 'customer-service',

	// It is important to keep this component consistent across the routes so
	// that React doesn't unmount it when switching between the routes and the
	// full state is kept so that scroll position and loaded data is kept when
	// going back from issue details to the queues page.
	entryPoint() {
		return fg('csm_appification_standalone_navigation')
			? csmCustomIssuePageEntryPoint
			: notFoundPageEntrypoint;
	},

	isHydrateRootEnabled: false,
	supportsPreviewPanel: true,

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
